import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { isEmbeddedDiscovery, resolveEmbeddedFlowProps } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { Sel, STag } from '#~/stag/tag.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { resolveControlSettings } from '../control/normalize.js';
import { useWiring } from '../wiring.js';
import { Panel } from '../store/types.js';

/** Manufacturer `<Footer>` component. */
export const Footer = (props: _Footer.Props): ReactElement => {
  const producer = useProducer();
  const wiring = useWiring();
  const finalSave = useStore((µ) => µ.finalSave);
  const blocked = !!finalSave && (finalSave.rawPending || finalSave.status !== 'retryable');
  const isReady = useStore((µ) => µ.manufacturer.isReady);
  const sourceSelection = useStore((µ) => µ.source.selection);
  const candidateModel = useStore((µ) => µ.candidate.modelId);
  const roomId = useStore((µ) => µ.roomId);
  const manufacturerConfig = useStore((µ) => µ.manufacturer.config);

  // ENG-3796: the first-run funnel replaces the Connect controls PANEL with its
  // own connection test, so this step is the last one the shared workflow owns
  // there. Undefined for every other caller, which is why the branch below
  // cannot change the normal Add Video Source flow.
  const onboarding = useStore((µ) => µ.onboarding);
  const onboardingExit = useStore(
    (µ) => ({
      candidate: µ.candidate,
      agentId: µ.agentId,
      selection: µ.source.selection,
      template: µ.manufacturer.config,
      defaultName: µ.manufacturer.defaultName
    }),
    shallow
  );

  const { track } = useAnalytics();

  const handleBack = useLatestFn(() => {
    producer((draft) => {
      // The Manufacturer step now sits AFTER Video (Video → Manufacturer →
      // Control for non-RTSP sources without a modelId), so Back returns to Video.
      draft.panel = Panel.Video;
    });
  });

  const handleNext = useLatestFn(() => {
    if (sourceSelection && candidateModel) {
      const deviceAnalyticsCatalogContext = resolveDeviceAnalyticsCatalogContextFromModel(
        manufacturerConfig?.model,
        candidateModel
      );

      // Per ENG-3960 §E12 — modelId (camelCase) normalized to device_model_id (canonical).
      // Per ENG-3960 §1.7 — additively stamp is_embedded + linking_surface when the
      // in-flight discovery is embedded; the generic path emits unchanged (no double-fire).
      track(EVENT_NAMES.DEVICE_CONTINUE_FROM_BRAND, EVENT_CATEGORIES.DEVICE, {
        deviceId: sourceSelection.id,
        studio_id: roomId,
        ...deviceAnalyticsCatalogContext,
        ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection))
      });
    }

    if (onboarding !== undefined) {
      if (!isReady || blocked) return;

      // Seed the control settings the Connect controls panel would have
      // produced, then COMMIT THROUGH THE WORKFLOW'S OWN ADD. The funnel used
      // to add the camera a second time from a rebuilt configuration, and that
      // second add is where every field went missing — the control endpoint,
      // the model, the host. One add, the production one.
      producer((draft) => {
        // A retry owns the original settings; same-tick submits cannot reseed them.
        if (draft.finalSave) return;
        draft.candidate.control = resolveControlSettings(
          draft.candidate.control,
          draft.manufacturer.config,
          draft.candidate.video
        ) as never;
      });

      void (async (): Promise<void> => {
        const deviceId = await wiring.handleCommit();

        // Only the workflow's saved receipt permits the onboarding handoff.
        if (deviceId) onboarding.onConfigured({ ...onboardingExit, deviceId: deviceId as never });
      })();

      return;
    }

    producer((draft) => {
      if (draft.manufacturer.isReady) {
        draft.panel++;
      }
    });
  });

  return (
    <_Footer.Root {...props}>
      <_Footer.Back onClick={handleBack} />
      <_Footer.Next
        disabled={!isReady || (onboarding !== undefined && blocked)}
        // Onboarding's next screen is the connection test, so the button says
        // what it does. Every other caller keeps the default "Continue".
        {...(onboarding === undefined ? {} : { label: 'Test connection' })}
        onClick={handleNext}
        {...STag(Sel.ConfigVideoDeviceBrandModel, 'footer.continue')}
      />
    </_Footer.Root>
  );
};
